import React from 'react';

import { ACCEPTED_PART_EXTENSIONS } from './usePartUpload';

// Native picker filter derived from the module's accepted extensions — so the
// picker and the upload validator (isAcceptedPartFile) can't drift apart.
const DEFAULT_ACCEPT = ACCEPTED_PART_EXTENSIONS.map((e) => `.${e}`).join(',');

export interface PartUploadFieldProps {
  /** Whether the control is interactive (mirrors the feature's enabled gate). */
  enabled: boolean;
  uploading: boolean;
  /** 0–1 byte progress while `uploading`. */
  progress: number;
  error: string | null;
  /** Filename of the current part, or '' when none is uploaded. */
  meshName: string;
  /** A part file was chosen — the parent uploads it (e.g. via usePartUpload). */
  onSelect: (file: File) => void;
  /** The uploaded part was removed. */
  onRemove: () => void;
  /** Field label above the control. Default 'Part'. */
  label?: string;
  /** Helper line shown when no part is uploaded (and there's no error). */
  hint?: string;
  /** Call-to-action on the empty upload affordance. */
  ctaLabel?: string;
  /** Native file-picker filter. Defaults to the module's accepted part
   *  extensions (.stl/.step/.stp); override to narrow it for a feature. */
  accept?: string;
}

// Reusable STEP/STL upload control with a REAL determinate progress bar (driven
// by actual bytes sent — usePartUpload's XHR upload.onprogress — not a fake
// timer). Feature-agnostic: all copy is configurable and the styling matches the
// feature-tree panels. Extracted from the lattice converter (#315, Level 2) so
// any part-ingesting feature gets the same upload + progress UX for free.
export function PartUploadField({
  enabled,
  uploading,
  progress,
  error,
  meshName,
  onSelect,
  onRemove,
  label = 'Part',
  hint,
  ctaLabel = 'Upload Part (.stl / .step)',
  accept = DEFAULT_ACCEPT,
}: PartUploadFieldProps) {
  const hasMesh = meshName !== '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onSelect(file);
  };

  return (
    <div className="space-y-1">
      <span className="text-xs text-telekinesis-neutral-50">{label}</span>
      {hasMesh ? (
        <div className="flex items-center gap-2">
          <span
            className="flex-1 truncate text-xs text-[#107d40]"
            title={meshName}
          >
            {meshName}
          </span>
          <button
            type="button"
            disabled={!enabled || uploading}
            onClick={onRemove}
            className="rounded-md border border-telekinesis-neutral-700 px-2 py-1 text-[10px] text-telekinesis-neutral-50 hover:border-[#107d40] hover:text-[#107d40] disabled:opacity-40"
          >
            Remove
          </button>
        </div>
      ) : (
        <label
          className={`flex h-8 w-full cursor-pointer items-center justify-center rounded-md border border-dashed border-telekinesis-neutral-700 text-xs text-telekinesis-neutral-50 hover:border-[#107d40] hover:text-[#107d40] ${
            !enabled || uploading ? 'cursor-not-allowed opacity-40' : ''
          }`}
        >
          {uploading ? 'Uploading…' : ctaLabel}
          <input
            type="file"
            accept={accept}
            className="hidden"
            disabled={!enabled || uploading}
            onChange={handleChange}
          />
        </label>
      )}

      {/* Real upload progress — driven by actual bytes sent, not a fake timer.
          Determinate because total bytes are known. */}
      {uploading && (
        <div className="space-y-1 pt-1">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-telekinesis-neutral-50">
              Uploading part…
            </p>
            <span className="text-[10px] tabular-nums text-[#107d40]">
              {Math.round(progress * 100)}%
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-telekinesis-neutral-800">
            <div
              className="h-full rounded-full bg-[#107d40] transition-[width] duration-150 ease-out"
              style={{ width: `${Math.max(3, progress * 100)}%` }}
            />
          </div>
        </div>
      )}

      {error && <p className="text-[10px] text-red-400">{error}</p>}
      {!hasMesh && !error && !uploading && hint && (
        <p className="text-[10px] leading-snug text-telekinesis-neutral-400">
          {hint}
        </p>
      )}
    </div>
  );
}
